import { type Dispatch, type SetStateAction } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import PurchasableItemsCard from '@/components/event-form/PurchasableItemsCard'
import FundraisingProgressFields from '@/components/event-form/FundraisingProgressFields'
import type { EventFormEventData, PurchasableItemRowState } from '@/components/event-form/shared'

export type SellingTab = 'tickets' | 'sponsorships' | 'ads' | 'donations'

export default function SellingSections({
  activeTab,
  onTabChange,
  rows,
  setRows,
  stripePaymentReady,
  fieldError,
  value,
  onChange,
}: {
  activeTab: SellingTab
  onTabChange: (tab: SellingTab) => void
  rows: PurchasableItemRowState[]
  setRows: Dispatch<SetStateAction<PurchasableItemRowState[]>>
  stripePaymentReady: boolean
  fieldError: (field: string, tierId?: string) => string | undefined
  value: EventFormEventData
  onChange: (next: EventFormEventData) => void
}) {
  return (
    <Tabs value={activeTab} onValueChange={(value) => onTabChange(value as SellingTab)} className="gap-0">
      <div className="border-b border-black/10">
        <TabsList variant="line" className="h-10 w-full justify-start rounded-none bg-transparent p-0">
          <TabsTrigger value="sponsorships" className="h-10 flex-none px-3 text-sm">
            Sponsorships
          </TabsTrigger>
          <TabsTrigger value="ads" className="h-10 flex-none px-3 text-sm">
            Ads
          </TabsTrigger>
          <TabsTrigger value="donations" className="h-10 flex-none px-3 text-sm">
            Donations
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="sponsorships" className="pt-4">
        <PurchasableItemsCard
          kind="sponsorship"
          title="Sponsorship levels"
          description="Add sponsorship levels with limited slots."
          rows={rows.filter((row) => row.kind === 'sponsorship')}
          setAllRows={setRows}
          rowLabel="Level"
          ctaLabel="Add level"
          namePlaceholder="Gold Sponsor"
          descriptionPlaceholder="- Logo on materials"
          removedNoun="Sponsorship"
          stripePaymentReady={stripePaymentReady}
          stripeBlurb="Connect Stripe to sell sponsorships through Gala Season."
          fieldError={fieldError}
        />
      </TabsContent>
      <TabsContent value="ads" className="pt-4">
        <PurchasableItemsCard
          kind="ad"
          title="Ads"
          description="Sell print or digital program-book ads."
          rows={rows.filter((row) => row.kind === 'ad')}
          setAllRows={setRows}
          rowLabel="Ad slot"
          ctaLabel="Add ad slot"
          namePlaceholder="Half-page Print Ad"
          descriptionPlaceholder="- Color, full bleed"
          removedNoun="Ad slot"
          stripePaymentReady={stripePaymentReady}
          stripeBlurb="Connect Stripe to sell ads through Gala Season."
          fieldError={fieldError}
          showAdFormat
        />
      </TabsContent>
      <TabsContent value="donations" className="pt-4">
        <EventDonationCard
          stripePaymentReady={stripePaymentReady}
          value={value}
          onChange={onChange}
          fieldError={fieldError}
        />
      </TabsContent>
    </Tabs>
  )
}

function EventDonationCard({
  stripePaymentReady,
  value,
  onChange,
  fieldError,
}: {
  stripePaymentReady: boolean
  value: EventFormEventData
  onChange: (next: EventFormEventData) => void
  fieldError: (field: string, tierId?: string) => string | undefined
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Donations</CardTitle>
      </CardHeader>
      <CardContent>
        {!stripePaymentReady ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            Connect Stripe to accept donations through Gala Season.{' '}
            <a href="/dashboard/settings/payments" className="font-medium underline">Set up payments</a>.
          </div>
        ) : (
          <>
            <p className="mb-4 text-sm text-muted-foreground">
              Accept donations on this event page. Donors get a confirmation email; your org issues the tax receipt.
            </p>
            <div className="flex items-center gap-2">
              <Switch
                id="donations_enabled"
                checked={value.donations_enabled}
                onCheckedChange={(checked) => onChange({ ...value, donations_enabled: checked })}
              />
              <Label htmlFor="donations_enabled">Enable donations</Label>
            </div>
            <div className="mt-4 border-t border-black/5 pt-4">
              <FundraisingProgressFields value={value} onChange={onChange} fieldError={fieldError} />
            </div>
          </>
        )}
        {fieldError('donations_enabled') && (
          <p className="mt-3 text-base text-destructive">{fieldError('donations_enabled')}</p>
        )}
      </CardContent>
    </Card>
  )
}
